import useFeatureTags from "@custom-hooks/useFeatureTags";
import { getIt } from "@uikit/getIt";
import { JsonUtil } from "imagica-corekit/dist/base/cutil/JsonUtil";
import { StudioProject } from "imagica-corekit/dist/base/project/domain/StudioProject";
import { PersonalFunctionStore } from "imagica-corekit/dist/base/store/PersonalFunctionStore";
import { StoryTransformer } from "imagica-corekit/dist/base/storyV2/Transformer/StoryTransformer";
import { Story } from "imagica-corekit/dist/base/storyV2/domain/Story";
import { EdgeState, StoryEdge } from "imagica-corekit/dist/base/storyV2/domain/StoryEdge";
import { StoryEventObject } from "imagica-corekit/dist/base/storyV2/domain/StoryEventObject";
import { StoryManager } from "imagica-corekit/dist/base/storyV2/domain/StoryManager";
import { StoryNode } from "imagica-corekit/dist/base/storyV2/domain/StoryNode";
import { StoryNodeState } from "imagica-corekit/dist/base/storyV2/domain/StoryNodeState";
import { StoryUtil } from "imagica-corekit/dist/base/storyV2/domain/StoryUtil";
import { chain, get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useReactFlow } from "reactflow";
import { useHomeUsePluginContainer } from "../../custom-hooks/HomeUsePluginRoot/container";
import store from "../../store";
import { editorActions } from "../../store/editor";
import func from "@uikit/func";
import {
  COMPLETE_LINE_STYLE,
  COMPLETE_MARKER_END,
  EDGE_STATE_CONFIG,
  ERROR_LINE_STYLE,
  ERROR_MARKER_END,
  GROUPID_REG,
  NEW_EDGE_REG,
  TRANSPARENT_MARKER_END,
} from "../../views/thinking-layout-editor/constants";
import { Identifier } from "imagica-corekit/dist/base/api/IdentifierTyped/Identifier";

type ArgsType = {
  getSaveData: <T>(templateObj: object, edges: any, variableList: any, params: object, updateParam: T) => object;
  getHomeEdges: () => void;
  setNodes: (node: any) => any;
  setEdges: (edge: any) => any;
  handleCustomToGroup: (preNodeId: string, newNodeArr: Array<any>) => any;
  errorClosure?: (nodeOrEdge: StoryNode | StoryEdge) => void;
  handleGroupToCustom: (preNodeId: string, newNode: any) => any;
  handleSave: (data: any) => any;
};

export default function useNewEdgeRun({
  getSaveData,
  getHomeEdges,
  setNodes,
  setEdges,
  handleCustomToGroup,
  errorClosure,
  handleGroupToCustom,
  handleSave,
}: ArgsType) {
  const { getNodes } = useReactFlow();
  const {
    nodeIndexRef,
    edgeIndexRef,
    nodesState: { nodesRef },
  } = useHomeUsePluginContainer();
  const dispatch = useDispatch();
  const getShowTooltip = useSelector((state: any) => state.editor.showTooltip);
  const setCloseToolTipArr = (val: any) => {
    dispatch(editorActions.setCloseToolTipArr(val));
  };
  const setShowTooltip = (val: any) => {
    dispatch(editorActions.setShowTooltip(val));
  };
  const setNeedDisableEdgeIds = (val: any) => {
    dispatch(editorActions.setNeedDisableEdgeIds(val));
  };

  const setIsEdgeRunning = (val: any) => {
    dispatch(editorActions.setIsEdgeRunning(val));
  };

  const featureTags = useFeatureTags();

  const getStory = <T>(edgeId?: string, updateParam?: T) => {
    const templateObj = store.getState().studio.selectedTemplate;
    const edges = getHomeEdges();
    const variableList = store.getState().fot.variableList;
    const data = getSaveData(templateObj, edges, variableList, {}, updateParam);
    const projectObj = { attributes: data, attrIndexer: (data as any).index, id: (templateObj as any).id };
    const projectJsonStr = JsonUtil.stringify(projectObj);
    const project = JsonUtil.toModelFromType(StudioProject, projectJsonStr);
    if (!project)
      return {
        story: undefined,
        storyEdge: undefined,
      };
    const story = StoryUtil.from(project, true, nodeIndexRef.current);
    story.graphData = JsonUtil.parseFromInstance(data);
    getIt(PersonalFunctionStore).extractFuncs(project);
    if (func.isEmpty(edgeId))
      return {
        story,
        storyEdge: undefined,
      };

    const storyEdge = story
      .findAllEdges()
      .filter(x => x.options.id === edgeId)
      .first();
    return {
      story,
      storyEdge,
    };
  };

  const handleUIdata = (nodeId: string, getSimpleNodeData: (node: any) => any) => {
    const nodes = getNodes();
    const currentNode = nodes.find(x => x.id === nodeId);
    if (func.isEmpty(currentNode)) return {};
    const data = getSimpleNodeData(currentNode);
    return {
      id: nodeId,
      type: currentNode?.type,
      data,
    };
  };

  type dataType = {
    id: string;
    type: string;
    data: any;
  };
  const updateNodeData = (updateNode: StoryNode, transformData: dataType | Array<dataType>) => {
    const preNodeId = updateNode.options.id;
    if (typeof transformData === "object" && !Array.isArray(transformData)) {
      if (
        updateNode.id !== undefined &&
        updateNode.options.id !== updateNode.id &&
        updateNode.options.id.includes("group")
      ) {
        /// group变node
        const newData = transformData;
        newData.id = updateNode.id;
        handleGroupToCustom(preNodeId, newData);
        handleNodeAndEdgeRef([transformData]);
        handleToolTip([transformData], updateNode.state);
        return;
      }
      const object = transformData as dataType;
      if (func.isEmpty(object.id) || func.isEmpty(object.data)) return;
      setNodes((prevList: any) => {
        return prevList.map((node: any) => {
          if (node.id === object.id) {
            return {
              ...node,
              type: object.type,
              data: {
                ...node.data,
                ...object.data,
              },
            };
          }
          return node;
        });
      });
      handleNodeAndEdgeRef([transformData]);
      handleToolTip([transformData], updateNode.state);
    } else {
      handleCustomToGroup(preNodeId, transformData);
      handleNodeAndEdgeRef(transformData);
      handleToolTip(transformData, updateNode.state);
    }
  };

  const handleNodeAndEdgeRef = (transformData: Array<dataType>) => {
    const customNode = transformData.filter(
      (x: any) => !func.isEmpty(x.data?.nodeIndexRef) || !func.isEmpty(x.data?.edgeIndexRef)
    );
    if (func.isEmpty(customNode)) return;
    const corekitNodeIndex = customNode[0].data.nodeIndexRef.current;
    const corekitEdgeIndex = customNode[0].data.edgeIndexRef.current;
    if (!isNaN(corekitNodeIndex) && corekitNodeIndex > nodeIndexRef.current) nodeIndexRef.current = corekitNodeIndex;
    if (!isNaN(corekitEdgeIndex) && corekitEdgeIndex > edgeIndexRef.current) edgeIndexRef.current = corekitEdgeIndex;
  };
  const updateLineState = (
    id: string,
    state: EdgeState,
    uselessLineIds: string[],
    lineId: {
      sourceIds: string[];
      target: string;
    }
  ) => {
    if (func.isEmpty(id) || func.isEmpty(state)) return;
    // 修改line的状态
    setEdges((prevList: any) => {
      return prevList.map((edge: any) => {
        if (lineId.sourceIds.includes(edge.id) || edge.id === lineId.target) {
          let lineStyle =
            state === EdgeState.LOADING
              ? COMPLETE_LINE_STYLE
              : state === EdgeState.ERROR
              ? ERROR_LINE_STYLE
              : COMPLETE_LINE_STYLE;
          if (uselessLineIds.includes(edge.id)) {
            lineStyle = ERROR_LINE_STYLE;
          }
          return {
            ...edge,
            animated: state === EdgeState.LOADING,
            style: lineStyle,
            markerEnd: lineId.sourceIds.includes(edge.id)
              ? TRANSPARENT_MARKER_END
              : state === EdgeState.LOADING
              ? COMPLETE_MARKER_END
              : state === EdgeState.ERROR
              ? ERROR_MARKER_END
              : COMPLETE_MARKER_END,
            data: {
              ...edge.data,
              stateConfig:
                state === EdgeState.LOADING ? EDGE_STATE_CONFIG[EdgeState.COMPLETED] : EDGE_STATE_CONFIG[state],
            },
          };
        }
        return edge;
      });
    });
  };
  const updataEdgeRunDisable = (id: string, state: EdgeState) => {
    const needDisableEdgeIds = store.getState().editor.needDisableEdgeIds;
    // 加载中或者完成
    if (state === EdgeState.LOADING || state === EdgeState.COMPLETED) {
      const closeRunDisable = needDisableEdgeIds.filter((x: any) => x !== id);
      setNeedDisableEdgeIds(closeRunDisable);
    }

    // 失败
    if (state === EdgeState.ERROR) {
      setNeedDisableEdgeIds([]);
    }
  };
  const updateEdgeState = (id: string, state: EdgeState, identifier?: Identifier) => {
    if (func.isEmpty(id) || func.isEmpty(state)) return;
    // 修改边的状态及identifier(可选)
    setNodes((prevList: any) => {
      return prevList.map((edge: any) => {
        if (edge.id === id) {
          return {
            ...edge,
            data: {
              ...edge.data,
              lineParam: {
                ...edge.data.lineParam,
                loading: state === EdgeState.LOADING,
                identifier: identifier ?? edge.data.lineParam.identifier,
              },
            },
          };
        }
        return edge;
      });
    });
  };

  const updateEdgeBlueprintMessage = (id: string | undefined, message: string) => {
    if (func.isEmpty(id) || func.isEmpty(message)) return;
    const newNodes = chain(nodesRef)
      .get("current")
      .map(edge => {
        if (edge.id === id) {
          const blueprintMessages: string[] = get(edge, "data.blueprintMessages", []);
          if (message === "done" && blueprintMessages.includes("done")) {
            return edge;
          }
          blueprintMessages.push(message);
          return {
            ...edge,
            data: {
              ...edge.data,
              blueprintMessages,
            },
          };
        }
        return edge;
      })
      .value();
    setNodes(newNodes);
  };

  const handleToolTip = (transformData: Array<dataType>, state: StoryNodeState) => {
    //运行完后才显示toolTip
    if (state !== StoryNodeState.COMPLETED && state !== StoryNodeState.ERROR) return;
    if (getShowTooltip === "new-edge-2") {
      setCloseToolTipArr(["new-edge-2"]);
      const nodeStr = transformData.some((x: any) => x.id === "group-3") ? "group-3" : "editor-3";
      setShowTooltip(nodeStr);
    }
  };
  const handleResolveRun = (param: {
    isRunAll: boolean;
    updateNode: any;
    transformData: any;
    resolve: (res?: any) => void;
  }) => {
    let { isRunAll, updateNode, transformData, resolve } = param;
    const isNodeCompleted = (updateNode.state as any) === EdgeState.COMPLETED;

    if (!isRunAll && isNodeCompleted) {
      resolve({
        updateNode,
        transformData,
      });
    }
  };
  const listenContent = (params: {
    isRunAll: boolean;
    story: Story;
    getSimpleNodeData: (node: any) => any;
    resolve: (res?: any) => void;
    reject: (error?: any) => void;
    edgeId?: string;
  }) => {
    const { isRunAll, story, getSimpleNodeData, resolve, reject, edgeId } = params;
    story.on((eventobject: any) => {
      // console.log("corekit story update");
      eventobject.elements.forEach((element: any) => {
        const updateNode = StoryEventObject.asNode(element);
        if (updateNode) {
          //group node，uiData传{}
          let uiData = {};
          if (!GROUPID_REG.test(updateNode.options.id) && !NEW_EDGE_REG.test(updateNode.options.id)) {
            const data = handleUIdata(updateNode.options.id, getSimpleNodeData);
            if (!func.isEmpty(data)) uiData = data;
          }
          // console.log("corekit node update", updateNode, uiData);
          const transformData = StoryTransformer.transform(uiData, updateNode, story);

          const blueprintMessage = get(transformData, "data.blueprintMessage");
          if (blueprintMessage) {
            updateEdgeBlueprintMessage(edgeId, blueprintMessage);
          }

          updateNodeData(updateNode, transformData as dataType | Array<dataType>);
          // console.log("corekit transformData:", transformData);
          if (errorClosure !== undefined && updateNode.state === StoryNodeState.ERROR) {
            errorClosure(updateNode);
          }
          handleResolveRun({
            isRunAll,
            updateNode,
            transformData,
            resolve,
          });
        }

        const updateEdge = StoryEventObject.asEdge(element);
        if (updateEdge) {
          const useLessLine = getUselessLineIds(story, updateEdge);
          const identifier = {
            type: updateEdge.options.actionData.identifierType,
            value: updateEdge.options.actionData.identifierValue,
          };
          updataEdgeRunDisable(updateEdge.options.id, updateEdge.state);
          updateEdgeState(updateEdge.options.id, updateEdge.state, identifier);
          updateLineState(updateEdge.options.id, updateEdge.state, useLessLine, {
            sourceIds: updateEdge.options.source,
            target: updateEdge.options.target,
          });
          if (errorClosure !== undefined && updateEdge.state === EdgeState.ERROR) {
            errorClosure(updateEdge);
          }
          // console.log("corekit edge update:", updateEdge.options.id, updateEdge.state);
        }
      });
    });
  };

  /** 找到对于带/的边，无效输入的line id */
  function getUselessLineIds(story: Story, currentEdge: StoryEdge) {
    const edgeText = currentEdge.options.actionData.enterText;
    let useSouceLines = currentEdge.options.source;
    /// 带/ 的边 并且有多个输入node
    if (edgeText.startsWith("/") && useSouceLines.length > 1) {
      // 获取sorcenode 和corekit中一样
      const usedSourceNode = StoryUtil.findEdgeSoure(story, currentEdge);
      useSouceLines = useSouceLines.filter(lineId => {
        return !usedSourceNode?.lines.find(nodeLine => {
          return nodeLine.options.id === lineId;
        });
      });
      return useSouceLines;
    }
    return [];
  }

  const corekitRun = async <T>(param: {
    getSimpleNodeData: (node: any) => any;
    edgeId?: string;
    updateParam?: T;
    isFromPreview?: Boolean;
    isRunAll?: boolean;
  }) => {
    if (featureTags.enableGlobalContext) {
      updateEdgeState(param.edgeId ?? "", EdgeState.IDLE);
      const saveResult = handleSave({ background: true });
      updateEdgeState(param.edgeId ?? "", EdgeState.LOADING);
      try {
        await saveResult;
      } catch (error) {
        console.warn(error);
      }
    }

    return new Promise((resolve: (res?: any) => void, reject: (error?: any) => void) => {
      const { getSimpleNodeData, edgeId, updateParam, isFromPreview = false, isRunAll = false } = param;
      const { story, storyEdge } = getStory(edgeId, updateParam);
      const storyManager: StoryManager = getIt(StoryManager);
      if (!story) return resolve();

      listenContent({
        isRunAll,
        story,
        getSimpleNodeData,
        resolve,
        reject,
        edgeId,
      });

      if (storyEdge) {
        setShowTooltip("close");
        storyManager.runEdge(story, storyEdge).finally(() => {
          setIsEdgeRunning(false);
        });
      } else {
        const needRunAll = !isFromPreview;
        const cleanContent = !needRunAll;
        storyManager.run(story, needRunAll, cleanContent).finally(() => {
          resolve();
          setIsEdgeRunning(false);
        });
      }
    });
  };

  const corekitRunStop = (projectId: string, edgeId: string) => {
    if (func.isEmpty(edgeId)) return;
    const storyManager = getIt(StoryManager);
    storyManager.stop(projectId, edgeId);
    setIsEdgeRunning(false);
  };

  return {
    corekitRun,
    corekitRunStop,
    updateEdgeState,
  };
}
